import { observer } from "mobx-react-lite";
import { useContext, useMemo } from "react";
import { FormRenderProps } from "react-final-form";

import { Stack, useScreenSize } from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { FormTemplateTypeCode } from "@libs/gateways/forms/FormsGateway.dtos.ts";
import { FormWarningClaimPending } from "@modules/forms/components/FormWarningClaimPending.tsx";
import { PatientDemographicContext } from "@modules/practice/screens/contact-details/context/PatientDemographicContext.ts";
import { ContactPreferences } from "@stores/comms/models/ContactPreferences.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { PatientNotice } from "@stores/practice/models/PatientNotice.ts";
import { PatientSetting } from "@stores/user-experience/models/PatientSetting.ts";
import { When } from "@ui-components/withPerm.tsx";

import { Labels } from "../../../shared-components/types/labels.enums.types.ts";
import { PatientCardIds } from "../../../shared-components/types/patient-card-ids.enum.ts";
import { ContactSubmissionFormDialog } from "../../shared-components/edit/ContactSubmissionFormDialog.tsx";
import { EditAccountBillTo } from "../../shared-components/edit/EditAccountBillTo.tsx";
import { EditCommsAndPreferences } from "../../shared-components/edit/EditCommsAndPreferences.tsx";
import { EditEntitlements } from "../../shared-components/edit/EditEntitlements.tsx";
import { EditFormLayout } from "../../shared-components/edit/EditFormLayout.tsx";
import { EditHealthProviders } from "../../shared-components/edit/EditHealthProviders.tsx";
import { EditPatientHeader } from "../../shared-components/edit/EditPatientHeader.tsx";
import { EditProfile } from "../../shared-components/edit/EditProfile.tsx";
import { EditRelationships } from "../../shared-components/edit/EditRelationships.tsx";
import { EditEmployers } from "../../shared-components/edit/employers/EditEmployers.tsx";
import { PatientEditFormValues as PatientFormValues } from "../../shared-components/edit/PatientEditFormValues.tsx";
import { getPatientFormValues } from "../../shared-components/edit/utils.tsx";
import { PatientFormValidator } from "../../shared-components/edit/validators/PatientFormValidator.tsx";
import { PreferencesAndConsentsValidator } from "../../shared-components/edit/validators/PreferencesAndConsentsValidator.ts";
import { getMenuItems, onSubmit } from "./utils.ts";

export interface PatientFormDialogProps {
  contact: Contact | undefined;
  contactPreferences: ContactPreferences | undefined;
  patientNotices: PatientNotice[] | undefined;
  patientSetting: PatientSetting | undefined;
}

const preferencesConsentsValidator = new PreferencesAndConsentsValidator();

export const PatientFormDialog: React.FC<PatientFormDialogProps> = observer(
  ({ contact, contactPreferences, patientNotices, patientSetting }) => {
    const root = useStores();
    const { core, practice, userExperience } = root;

    const {
      ref: { languages },
      ui: { demographicInitialValues, newContactType }
    } = practice;

    const { height } = useScreenSize();
    const isNewPatientVisible = !!newContactType;
    const helper = useContext(PatientDemographicContext);
    const SIDE_MENU_HEIGHT_BREAK_POINT = 848;
    const sideMenuHeight = height <= SIDE_MENU_HEIGHT_BREAK_POINT;
    const commsPrefText = useMemo(
      () =>
        sideMenuHeight
          ? Labels.commsPreferences
          : Labels.communicationPreferences,
      [sideMenuHeight]
    );

    const menuItems = getMenuItems(commsPrefText);
    const permittedMenuItems = menuItems.filter(
      item => !item.permissions || core.hasPermissions(item.permissions)
    );

    const initialValues = useMemo(
      () =>
        getPatientFormValues(
          {
            contact,
            contactPreferences,
            patientSetting,
            isAddPatient: isNewPatientVisible
          },
          {
            core,
            practice,
            userExperience
          }
        ),
      [
        contact,
        core,
        isNewPatientVisible,
        practice,
        contactPreferences,
        userExperience,
        patientSetting
      ]
    );

    return (
      <ContactSubmissionFormDialog<PatientFormValues>
        initialValues={initialValues}
        validate={(values: PatientFormValues) => {
          if (core.tenantDetails) {
            const validator = new PatientFormValidator({
              countries: core.ref.countries.values,
              languages: languages.values,
              oldCscExpiry: initialValues.csc?.expiry,
              country: core.tenantDetails.country
            });
            return {
              ...validator.validate(values),
              ...preferencesConsentsValidator.validate(values)
            };
          }
          return undefined;
        }}
        onSubmit={values =>
          onSubmit(
            contact,
            { initialValues, values },
            { contactPreferences, helper, root }
          )
        }
        buttonsProps={() => ({
          cancelButtonProps: {
            id: "close-patient-form-dialog"
          },
          hideButtonsSeparator: true,
          disableSubmitOnPristine: !demographicInitialValues,
          styles: {
            root: { marginTop: 0 }
          }
        })}
      >
        {(props: FormRenderProps<PatientFormValues>) => {
          const { firstName, lastName } = props.values;
          return (
            <EditFormLayout menuItems={permittedMenuItems} contact={contact}>
              <Stack>
                {contact && (
                  <FormWarningClaimPending
                    patientId={contact.id}
                    formTemplateTypes={[
                      FormTemplateTypeCode.acc45Demographic,
                      FormTemplateTypeCode.patientDemographic
                    ]}
                  />
                )}
                <Stack tokens={{ childrenGap: 48 }} grow>
                  <EditPatientHeader
                    contact={contact}
                    patientNotices={patientNotices}
                    patientSetting={patientSetting}
                  />
                  <EditCommsAndPreferences contact={contact} />
                  <When permission={[Permission.AccountHistoryAllowed]}>
                    <EditAccountBillTo />
                  </When>
                  <EditRelationships
                    fullName={
                      firstName && lastName ? `${firstName} ${lastName}` : ""
                    }
                  />
                  <EditEntitlements />
                  <EditHealthProviders />
                  <EditEmployers contact={contact} />
                  <EditProfile
                    isAddNew={isNewPatientVisible}
                    contact={contact}
                    sectionId={PatientCardIds.patientProfile}
                  />
                </Stack>
              </Stack>
            </EditFormLayout>
          );
        }}
      </ContactSubmissionFormDialog>
    );
  }
);
